import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  isDevMode,
  importProvidersFrom, LOCALE_ID, inject
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling
} from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localePt from '@angular/common/locales/pt';

// Fonction pour détecter la langue du navigateur
function getBrowserLanguage(): string {
  const browserLang = (localStorage.getItem('lang')) ? localStorage.getItem('lang') : navigator.language.toLowerCase();
  if (browserLang.startsWith('fr')) return 'fr';
  if (browserLang.startsWith('pt')) return 'pt';
  return 'en'; // Anglais par défaut
}

// Fonction pour mapper la langue vers la locale
function getLocaleId(lang: string): string {
  const localeMap: { [key: string]: string } = {
    'fr': 'fr-FR',
    'en': 'en-US',
    'pt': 'pt-BR'
  };
  return localeMap[lang] || 'en-US';
}

const defaultLang = getBrowserLanguage();

import { routes } from './app.routes';
import CustomPreset from './custom.preset';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import {FilterMatchMode, MessageService, ConfirmationService} from 'primeng/api';
import {
  provideHttpClient, withInterceptors,
} from '@angular/common/http';
import { TranslocoHttpLoader } from './transloco-loader';
import {provideTransloco, TranslocoService} from '@jsverse/transloco';
import { PrimeModule } from './core/modules/prime-module';
import { CommonModule } from '@angular/common';
import { ContainerFileApi, ContainerImageApi, LoopBackAuth, SDKBrowserModule } from './shared/sdk';
import { StorageBrowser } from './shared/sdk';
import { AppAuthService } from './core/services/app.auth.service';
import { AuthGuard } from './core/guards/auth.guard';
import { ExtendedContainerFileApi } from './shared/extended/ExtendedContainerFile';
import { ExtendedContainerImageApi } from './shared/extended/ExtendedContainerImage';
import { AuthGuardAdmin } from './core/guards/auth.guard.admin';
import { AuthGuardUltranet } from './core/guards/auth.guard.ultranet';
import { AuthGuardEBusiness } from './core/guards/auth.guard.eBusiness';
import { DialogService } from 'primeng/dynamicdialog';
import { AppCacheService } from './core/services/app.cache.service';
import { addHeaderInterceptor } from './core/interceptors/interceptor.function';
import {AppToasterService} from './core/services/app.toaster.service';
import {progressInterceptor} from 'ngx-progressbar/http';
import {environment} from '../environments/environment';
import {AuthGuardJ30} from './core/guards/auth.guard.j30';
import {AuthGuardSpecial} from './core/guards/auth.guard.special';
import {AuthGuardSuperAdmin} from './core/guards/auth.guard.super-admin';
import {AuthGuardUser1} from './core/guards/auth.guard.user1';
import {provideModalCompat} from './core/components/ngBootstrap/ng20/modal-compat.tokens';
import {AppSessionService} from './core/services/app.session.service';
import {UrlSerializer} from '@angular/router';
import {TenantUrlSerializer} from './core/services/tenant-url.serializer';
import { AuthGuardManageOperations } from './core/guards/auth.guard.manageOperations';

export const appConfig: ApplicationConfig = {

  providers: [
    DialogService,
    AppAuthService, // S'assurer que le service est fourni
    AppSessionService,
    AppCacheService,
    MessageService,
    ConfirmationService,
    AppToasterService,
    TranslocoService,
    AuthGuard,
    AuthGuardAdmin,
    AuthGuardManageOperations,
    AuthGuardUltranet,
    AuthGuardEBusiness,
    AuthGuardJ30,
    AuthGuardSpecial,
    AuthGuardSuperAdmin,
    AuthGuardUser1,
    { provide: StorageBrowser, useClass: StorageBrowser },
    provideModalCompat({
      centered: true,
      scrollable: true,
      closeOnEscape: true,
      dismissableMask: false
    }),
    importProvidersFrom(SDKBrowserModule.forRoot(), PrimeModule, CommonModule),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }), withEnabledBlockingInitialNavigation()),
    // URLs par tenant : la barre d'adresse affiche /1Gnet, /REZnet, /TXnet…
    // (routes internes canoniques). VGE : no-op strict (labels = clés).
    { provide: UrlSerializer, useClass: TenantUrlSerializer },
    provideAnimationsAsync(),
    providePrimeNG({
      ripple: false,
      zIndex: {
        modal: 1200,    // dialog, sidebar
        overlay: 1000,  // dropdown, overlaypanel
        menu: 1000,     // overlay menus
        tooltip: 1100   // tooltip
      },
      filterMatchModeOptions: {
        text: [FilterMatchMode.STARTS_WITH, FilterMatchMode.CONTAINS, FilterMatchMode.NOT_CONTAINS, FilterMatchMode.ENDS_WITH, FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS],
        numeric: [FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS, FilterMatchMode.LESS_THAN, FilterMatchMode.LESS_THAN_OR_EQUAL_TO, FilterMatchMode.GREATER_THAN, FilterMatchMode.GREATER_THAN_OR_EQUAL_TO],
        date: [FilterMatchMode.DATE_IS, FilterMatchMode.DATE_IS_NOT, FilterMatchMode.DATE_BEFORE, FilterMatchMode.DATE_AFTER]
      },

      // theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } }

      theme: {
        options:{
          darkModeSelector: '.app-dark',
        },
        preset: CustomPreset
      }
    }),

    provideHttpClient(withInterceptors([addHeaderInterceptor, progressInterceptor])),

    { provide: ContainerFileApi, useClass: ExtendedContainerFileApi},
    { provide: ContainerImageApi, useClass: ExtendedContainerImageApi},

    { provide: LoopBackAuth, useClass: AppAuthService},
    { provide: LOCALE_ID, useValue: getLocaleId(defaultLang) },

    provideTransloco({
      config: {
        availableLangs: environment.languages,
        defaultLang: defaultLang, // Utiliser la langue détectée
        fallbackLang: ['en', 'fr'], // Anglais en fallback principal
        reRenderOnLangChange: true,
        missingHandler: {
          logMissingKey: false,
          useFallbackTranslation: false,
          allowEmpty: true
        },
        flatten: {
          aot: !isDevMode(),
        },
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    })
  ],
};
